import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { postsAPI, clientsAPI, usersAPI, agenciesAPI } from '../../services/api';
import { Card, StatusBadge, Button } from '../ui';
import Loading from '../common/Loading';
import { formatDate, getImageUrl } from '../../utils/helpers';
import StatsCard from './StatsCard';
import ClientIntroModal from '../clients/ClientIntroModal';
import {
  Plus,
  ArrowRight,
  ArrowLeft,
  Activity,
  Users,
  Clock,
  UserPlus,
  FilePlus,
  List,
} from 'lucide-react';

const Dashboard = () => {
  const { user, isClient, isAdmin, isSuperAdmin } = useAuth();
  const { isRTL, t } = useLanguage();
  const [stats, setStats] = useState({
    totalPosts: 0,
    pendingPosts: 0,
    approvedPosts: 0,
    needsChangesPosts: 0,
    totalClients: 0,
    totalDesigners: 0,
    totalWriters: 0,
  });
  const [recentPosts, setRecentPosts] = useState([]);
  const [clients, setClients] = useState([]);
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIntroModal, setShowIntroModal] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) {
      fetchDashboardData();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isClient && user) {
      const introSeen = localStorage.getItem('client_intro_seen');
      const userCreatedAt = user.created_at ? new Date(user.created_at) : null;
      const isNewUser =
        userCreatedAt &&
        Date.now() - userCreatedAt.getTime() < 7 * 24 * 60 * 60 * 1000;

      if (!introSeen && isNewUser) {
        setShowIntroModal(true);
      }
    }
  }, [isClient, user]);

  if (isSuperAdmin) {
    return <Navigate to="/super-admin/dashboard" replace />;
  }

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const postsResponse = await postsAPI.getAll();
      const posts = postsResponse.data.posts;

      let clientsData = [];
      if (!isClient) {
        const clientsResponse = await clientsAPI.getAll();
        clientsData = clientsResponse.data.clients || [];
        setClients(clientsData);
      }

      let usersData = [];
      let agencyData = null;
      if (isAdmin) {
        try {
          const [usersResponse, agencyResponse] = await Promise.all([
            usersAPI.getAll(),
            agenciesAPI.getMyAgency(),
          ]);
          usersData = usersResponse.data.users || [];
          agencyData = agencyResponse.data.agency;
          setAgency(agencyData);
        } catch (err) {
          console.error('Failed to fetch users/agency:', err);
        }
      }

      setStats({
        totalPosts: posts.length,
        pendingPosts: posts.filter((p) => p.status === 'pending').length,
        approvedPosts: posts.filter((p) => p.status === 'approved').length,
        needsChangesPosts: posts.filter((p) => p.status === 'needs_changes')
          .length,
        totalClients: clientsData.length,
        totalDesigners: usersData.filter((u) => u.role === 'designer').length,
        totalWriters: usersData.filter((u) => u.role === 'writer').length,
      });

      setRecentPosts(posts.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <>
      {showIntroModal && (
        <ClientIntroModal onClose={() => setShowIntroModal(false)} />
      )}

      <div className="space-y-6 animate-in fade-in duration-500">

        {/* Header Overview */}
        <Card className="p-6 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-5 h-5 text-sky-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-sky-600">
                  {isRTL ? 'نظرة عامة' : 'Overview'}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                {isRTL ? t('dashboard') : 'Dashboard'}
              </h2>
              <p className="text-slate-600 text-sm mt-1">
                {isRTL
                  ? `مرحبًا بعودتك، ${user?.name || ''}. إليك ملخص اليوم.`
                  : `Welcome back, ${user?.name || ''}. Here’s your summary for today.`}
              </p>
            </div>

            {!isClient && (
              <div className="flex flex-col items-start md:items-end gap-3">
                {agency && (
                  <div
                    className={`text-xs text-slate-500 ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  >
                    <p className="font-semibold text-slate-800">
                      {agency.company_name}
                    </p>
                    <p>
                      {isRTL ? 'العملاء النشطون:' : 'Active clients:'}{' '}
                      <span className="font-bold text-sky-600">
                        {stats.totalClients}
                      </span>
                    </p>
                  </div>
                )}
                <Button
                  onClick={() => (window.location.href = '/posts/new')}
                  variant="primary"
                  className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30"
                >
                  <Plus className="w-4 h-4" />
                  {t('createNewPost')}
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Stats Grid */}
        {isAdmin ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Temporarily hidden */}
            {/* <StatsCard
              title={t('totalClients')}
              value={stats.totalClients}
              color="blue"
              icon="💼"
              isRTL={isRTL}
            />
            <StatsCard
              title={t('totalPosts')}
              value={stats.totalPosts}
              color="yellow"
              icon="📄"
              isRTL={isRTL}
            /> */}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Temporarily hidden */}
            {/* <StatsCard
              title={t('totalPosts')}
              value={stats.totalPosts}
              color="blue"
              icon="📊"
              isRTL={isRTL}
            /> */}
            <StatsCard
              title={t('pendingApproval')}
              value={stats.pendingPosts}
              color="yellow"
              icon="⏳"
              isRTL={isRTL}
            />
            <StatsCard
              title={t('approved')}
              value={stats.approvedPosts}
              color="green"
              icon="✅"
              isRTL={isRTL}
            />
            <StatsCard
              title={t('needsChanges')}
              value={stats.needsChangesPosts}
              color="red"
              icon="🔄"
              isRTL={isRTL}
            />
          </div>
        )}

        {/* Extra Admin Stats */}
        {/* Extra Admin Stats */}
        {isAdmin && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatsCard
              title={t('pendingPosts')}
              value={stats.pendingPosts}
              color="yellow"
              icon="⏳"
              isRTL={isRTL}
            />
            <StatsCard
              title={t('approvedPosts')}
              value={stats.approvedPosts}
              color="green"
              icon="✅"
              isRTL={isRTL}
            />
            <StatsCard
              title={t('needsChanges')}
              value={stats.needsChangesPosts}
              color="red"
              icon="🔄"
              isRTL={isRTL}
            />
          </div>
        )}

        {/* My Clients */}
        {!isClient && clients.length > 0 && (
          <Card className="p-6 overflow-hidden">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <span className="flex w-10 h-10 items-center justify-center rounded-xl bg-gradient-to-br from-sky-100 to-emerald-100 text-sky-600">
                  <Users className="w-5 h-5" />
                </span>
                <div>
                  <h3
                    className={`font-bold text-slate-900 ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  >
                    {t('recentClients')}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {isRTL ? `${clients.length} عميل` : `${clients.length} clients`}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => (window.location.href = '/clients')}
                className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
              >
                {t('viewAll')}
                {isRTL ? (
                  <ArrowLeft className="w-4 h-4" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.slice(0, 6).map((client) => (
                <Link
                  key={client.id}
                  to={`/clients/${client.id}`}
                  className={`group flex items-center gap-4 p-4 rounded-xl border border-slate-100 bg-white hover:border-sky-200 hover:bg-gradient-to-br hover:from-sky-50/50 hover:to-white hover:shadow-md transition-all duration-200 ${
                    isRTL ? 'text-right flex-row-reverse' : 'text-left'
                  }`}
                >
                  {/* Logo */}
                  <div className="flex-shrink-0">
                    {client.logo_url ? (
                      <div className="relative w-14 h-14 rounded-xl overflow-hidden border-2 border-slate-100 bg-white shadow-sm group-hover:border-sky-100">
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-sky-50 to-emerald-50 text-sky-600 font-bold text-lg">
                          {client.company_name?.[0] || 'C'}
                        </div>
                        <img
                          src={getImageUrl(client.logo_url)}
                          alt={client.company_name}
                          className="relative z-10 w-full h-full object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    ) : (
                      <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br from-sky-50 to-emerald-50 text-sky-600 font-bold text-xl border border-slate-100">
                        {client.company_name?.[0] || 'C'}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate group-hover:text-sky-700 transition-colors">
                      {client.company_name}
                    </h3>
                    {client.contact_name && (
                      <p className="text-sm text-slate-500 truncate mt-0.5">
                        {client.contact_name}
                      </p>
                    )}
                  </div>

                  {/* Arrow hint */}
                  <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    {isRTL ? (
                      <ArrowLeft className="w-4 h-4 text-sky-500" />
                    ) : (
                      <ArrowRight className="w-4 h-4 text-sky-500" />
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        )}

        {/* Recent Posts / Needs Attention */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-sky-600" />
              <h3
                className={`font-bold text-slate-900 ${
                  isRTL ? 'text-right' : 'text-left'
                }`}
              >
                {recentPosts.filter((p) => p.status !== 'approved').length > 0
                  ? t('needsAttention') || 'Needs Attention'
                  : t('recentPosts')}
              </h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => (window.location.href = '/posts')}
            >
              {t('viewAll')}
              {isRTL ? (
                <ArrowLeft className="w-3 h-3" />
              ) : (
                <ArrowRight className="w-3 h-3" />
              )}
            </Button>
          </div>

          {recentPosts.length === 0 ? (
            <p
              className={`text-slate-500 text-center py-8 ${
                isRTL ? 'text-right' : 'text-left'
              }`}
            >
              {t('noPosts')}
            </p>
          ) : (
            <div className="space-y-3">
              {recentPosts.slice(0, 5).map((post) => (
                <Link
                  key={post.id}
                  to={`/posts/${post.id}`}
                  className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-lg transition-colors border border-transparent hover:border-slate-100 cursor-pointer"
                >
                  {/* Thumbnail */}
                  {post.current_image_url && (
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                      <img
                        src={getImageUrl(post.current_image_url)}
                        alt={post.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Title + meta */}
                  <div
                    className={`flex-1 min-w-0 ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  >
                    <h4 className="text-sm font-semibold text-slate-900 truncate">
                      {post.title}
                    </h4>
                    <p className="text-xs text-slate-500">
                      {post.client_name} • {formatDate(post.created_at)}
                    </p>
                  </div>

                  <StatusBadge status={post.status} size="sm" />
                </Link>
              ))}
            </div>
          )}
        </Card>

        {/* Quick Actions */}
        {!isClient && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3
                  className={`font-bold text-slate-900 ${
                    isRTL ? 'text-right' : 'text-left'
                  }`}
                >
                  {t('quickActions')}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {isRTL
                    ? 'اختصارات لتنفيذ أهم المهام بسرعة'
                    : 'Shortcuts to your most important actions'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {isAdmin && (
                <Link to="/clients/new">
                  <button className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300 px-4 py-3 text-left transition-all">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-sky-100 text-sky-600">
                      <UserPlus className="w-4 h-4" />
                    </span>
                    <span className="flex flex-col">
                      <span className="text-sm font-semibold text-slate-900">
                        {t('addNewClient')}
                      </span>
                      <span className="text-xs text-slate-500">
                        {isRTL
                          ? 'إنشاء عميل جديد وربطه بالفريق'
                          : 'Create a new client and assign your team'}
                      </span>
                    </span>
                  </button>
                </Link>
              )}

              <Link to="/posts/new">
                <button className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300 px-4 py-3 text-left transition-all">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <FilePlus className="w-4 h-4" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-semibold text-slate-900">
                      {t('createNewPost')}
                    </span>
                    <span className="text-xs text-slate-500">
                      {isRTL
                        ? 'إضافة منشور جديد لعميل حالي'
                        : 'Add a new post for a client'}
                    </span>
                  </span>
                </button>
              </Link>

              <Link to="/posts">
                <button className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 px-4 py-3 text-left transition-all">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                    <List className="w-4 h-4" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-semibold text-slate-900">
                      {t('viewAllPosts')}
                    </span>
                    <span className="text-xs text-slate-500">
                      {isRTL
                        ? 'عرض وإدارة جميع المنشورات'
                        : 'View and manage all posts'}
                    </span>
                  </span>
                </button>
              </Link>

              {isAdmin && (
                <Link to="/clients">
                  <button className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 px-4 py-3 text-left transition-all">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                      <Users className="w-4 h-4" />
                    </span>
                    <span className="flex flex-col">
                      <span className="text-sm font-semibold text-slate-900">
                        {t('viewAllClients')}
                      </span>
                      <span className="text-xs text-slate-500">
                        {isRTL
                          ? 'إدارة قائمة العملاء الحالية'
                          : 'Review and manage all clients'}
                      </span>
                    </span>
                  </button>
                </Link>
              )}
            </div>
          </Card>
        )}
      </div>
    </>
  );
};

export default Dashboard;